import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { ChevronRight, Home, AlertTriangle, Target, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';

const SITE_URL = 'https://www.practice4sat.com';

const questionTypes = [
  {
    number: 1,
    title: "Equations with Fractions",
    strategyName: "Clear fractions in one step",
    strategySteps: ["Find the LCD", "Multiply every term", "Solve the simplified equation"],
    commonTrap: "Sign errors during distribution",
    wrongApproach: "$-(2x + 1) \\rightarrow -2x + 1$",
    example: {
      problem: "Solve for $x$:",
      equation: "$$\\dfrac{x - 2}{3} - \\dfrac{2x + 1}{4} = \\dfrac{1}{12}$$",
      steps: [
        "LCD = 12",
        "$4(x - 2) - 3(2x + 1) = 1$",
        "$4x - 8 - 6x - 3 = 1$",
        "$-2x - 11 = 1$",
        "$x = -6$"
      ]
    },
    practice: {
      equation: "$$\\dfrac{2x + 3}{5} - \\dfrac{x - 1}{2} = \\dfrac{1}{10}$$",
      wrongApproach: "$-5(x - 1) \\rightarrow -5x + 1$",
      answer: "$x = 10$",
      explanation: [
        "$2(2x + 3) - 5(x - 1) = 1$",
        "$4x + 6 - 5x + 5 = 1$",
        "$-x + 11 = 1 \\Rightarrow x = 10$"
      ]
    },
    keyTakeaway: "Fractions are easy — sign mistakes are the real trap."
  },
  {
    number: 2,
    title: "Variables on Both Sides",
    strategyName: "Move variables first",
    strategySteps: ["Bring $x$ terms together", "Then move constants"],
    commonTrap: "Losing terms during expansion",
    wrongApproach: "Expand everything at once and make arithmetic mistakes",
    example: {
      problem: "Solve:",
      equation: "$$3(2x - 5) + 4 = 2(x + 1) + 3x$$",
      steps: [
        "$6x - 15 + 4 = 2x + 2 + 3x$",
        "$6x - 11 = 5x + 2$",
        "$x = 13$"
      ]
    },
    practice: {
      equation: "$$4(2x + 3) - x = 3(x + 5) + 2x$$",
      wrongApproach: "Drop or miscombine terms",
      answer: "$x = \\dfrac{3}{2}$",
      explanation: [
        "$8x + 12 - x = 3x + 15 + 2x$",
        "$7x + 12 = 5x + 15$",
        "$2x = 3 \\Rightarrow x = \\dfrac{3}{2}$"
      ]
    },
    keyTakeaway: "Most errors happen during expansion — go step by step."
  },
  {
    number: 3,
    title: "Word Problems \u2192 Linear Equations",
    strategyName: "Translate exactly",
    strategySteps: ["Let $x$ = unknown", "Translate carefully", "Use parentheses when needed"],
    commonTrap: "Misinterpreting \"times\" relationships",
    wrongApproach: null,
    example: {
      problem: "A number decreased by 4 is equal to three times the number increased by 2.",
      equation: null,
      wrongApproach: "$x - 4 = 3x + 2$",
      steps: [
        "$x - 4 = 3(x + 2)$",
        "$x - 4 = 3x + 6$",
        "$-10 = 2x \\Rightarrow x = -5$"
      ]
    },
    practice: {
      problem: "A number increased by 6 is equal to twice the number decreased by 3.",
      equation: null,
      wrongApproach: "$x + 6 = 2x - 3$",
      answer: "$x = 12$",
      explanation: [
        "$x + 6 = 2(x - 3)$",
        "$x + 6 = 2x - 6$",
        "$12 = x$"
      ]
    },
    keyTakeaway: "\"Times\" applies to the entire expression — use parentheses."
  },
  {
    number: 4,
    title: "Multi-Step Inequalities",
    strategyName: "Solve normally, flip at the end",
    strategySteps: ["Solve like an equation", "Flip only when dividing by a negative"],
    commonTrap: "Flipping too early",
    wrongApproach: "Flip the inequality during distribution",
    example: {
      problem: "Solve:",
      equation: "$$-2(2x - 3) + 1 \\ge 3x - 5$$",
      steps: [
        "$-4x + 6 + 1 \\ge 3x - 5$",
        "$-4x + 7 \\ge 3x - 5$",
        "$12 \\ge 7x$",
        "$x \\le \\dfrac{12}{7}$"
      ]
    },
    practice: {
      equation: "$$-3(x + 1) + 4 < 2x + 7$$",
      wrongApproach: "Flip too early or forget to flip",
      answer: "$x > -\\dfrac{6}{5}$",
      explanation: [
        "$-3x - 3 + 4 < 2x + 7$",
        "$-3x + 1 < 2x + 7$",
        "$-6 < 5x$",
        "$x > -\\dfrac{6}{5}$"
      ]
    },
    keyTakeaway: "Only flip when dividing by a negative — nowhere else."
  },
  {
    number: 5,
    title: "Parameter Questions",
    strategyName: "Match both sides exactly",
    strategySteps: ["Expand both sides", "Match coefficients and constants"],
    commonTrap: "Matching only one part",
    wrongApproach: "Stop after finding $k = 5$ without checking constants",
    example: {
      problem: "For what value of $k$ does the following equation have infinitely many solutions?",
      equation: "$$k(x + 2) = 5x + 10$$",
      steps: [
        "$kx + 2k = 5x + 10$",
        "Match: $k = 5$",
        "$2k = 10 \\Rightarrow k = 5$"
      ]
    },
    practice: {
      problem: "For what value of $a$ does the following equation have infinitely many solutions?",
      equation: "$$a(x - 3) = 4x - 12$$",
      wrongApproach: "Match only one term",
      answer: "$a = 4$",
      explanation: [
        "$ax - 3a = 4x - 12$",
        "$a = 4$"
      ]
    },
    keyTakeaway: "Infinite solutions mean both sides must match completely."
  }
];

const ctaItems = [
  { text: "Take the free diagnostic quiz.", url: "/quick-quiz" },
  { text: "Get a personalized study plan.", url: "/#pricing" },
  { text: "Practice targeted SAT questions.", url: "/#pricing" },
  { text: "Take full-length adaptive tests.", url: "/#pricing" },
  { text: "Learn with step-by-step solutions.", url: "/#pricing" },
  { text: "Start now on Practice4SAT.", url: "/" }
];

function PracticeReveal({ practice, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`practice-${idx}`} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '1.25rem', marginTop: '1rem' }}>
      <h4 style={{ fontWeight: 600, fontSize: '1.05rem', color: '#166534', margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>
        Try It Yourself
      </h4>
      {practice.problem && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .5rem 0', fontFamily: "'Arial', sans-serif" }}>{practice.problem}</p>}
      {practice.equation && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>{practice.equation}</p>}
      {practice.wrongApproach && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '.75rem 1rem', marginBottom: '.75rem' }}>
          <p style={{ margin: 0, fontSize: '.9rem', color: '#991b1b', fontFamily: "'Arial', sans-serif" }}>
            <strong>What Most Students Do (Wrong):</strong> {practice.wrongApproach}
          </p>
        </div>
      )}
      <button
        data-testid={`reveal-answer-${idx}`}
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: '.5rem',
          background: '#166534', color: '#fff', border: 'none', borderRadius: 6,
          padding: '.5rem 1rem', cursor: 'pointer', fontSize: '.95rem', fontWeight: 500,
          fontFamily: "'Arial', sans-serif"
        }}
      >
        {open ? 'Hide Answer' : 'Show Answer'}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div style={{ marginTop: '.75rem', padding: '.75rem', background: '#fff', borderRadius: 8, border: '1px solid #d1fae5' }}>
          <p style={{ fontWeight: 600, color: '#166534', margin: '0 0 .5rem 0', fontFamily: "'Arial', sans-serif" }}>
            Answer: {practice.answer}
          </p>
          <p style={{ fontWeight: 600, fontSize: '.95rem', color: '#374151', margin: '0 0 .25rem 0', fontFamily: "'Arial', sans-serif" }}>Quick Explanation:</p>
          {practice.explanation.map((step, i) => (
            <p key={`step-${i}`} style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .25rem 0', paddingLeft: '.5rem', fontFamily: "'Arial', sans-serif" }}>{step}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionTypeSection({ qt, idx }) {
  const bg = idx % 2 === 0 ? '#f8f9fa' : '#ffffff';
  return (
    <section
      aria-labelledby={`qt-${qt.number}`}
      data-testid={`question-type-${qt.number}`}
      style={{ background: bg, padding: '2rem 0' }}
    >
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 2rem' }}>
        <h2 id={`qt-${qt.number}`} style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0030b3', margin: '0 0 1.25rem 0', fontFamily: "'Arial', sans-serif" }}>
          {"Question Type " + qt.number + ": " + qt.title}
        </h2>

        {/* Strategy */}
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e40af', margin: '0 0 .5rem 0', display: 'flex', alignItems: 'center', gap: '.4rem', fontFamily: "'Arial', sans-serif" }}>
            <Target size={16} /> {"Strategy: " + qt.strategyName}
          </h3>
          <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {qt.strategySteps.map((s, i) => (
              <li key={`strategy-${i}`} style={{ color: '#1e3a5f', lineHeight: 1.6, fontFamily: "'Arial', sans-serif" }}>{s}</li>
            ))}
          </ol>
        </div>

        {/* Common Trap */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#991b1b', margin: '0 0 .25rem 0', display: 'flex', alignItems: 'center', gap: '.4rem', fontFamily: "'Arial', sans-serif" }}>
            <AlertTriangle size={16} /> {"Common Trap: " + qt.commonTrap}
          </h3>
          {qt.wrongApproach && (
            <p style={{ margin: '.5rem 0 0 0', fontSize: '.95rem', color: '#7f1d1d', fontFamily: "'Arial', sans-serif" }}>
              <strong>What Most Students Do (Wrong):</strong> {qt.wrongApproach}
            </p>
          )}
        </div>

        {/* Example */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#374151', margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>Example</h3>
          <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .25rem 0', fontFamily: "'Arial', sans-serif" }}>{qt.example.problem}</p>
          {qt.example.equation && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>{qt.example.equation}</p>}
          {qt.example.wrongApproach && (
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: '.5rem .75rem', marginBottom: '.75rem' }}>
              <p style={{ margin: 0, fontSize: '.9rem', color: '#991b1b', fontFamily: "'Arial', sans-serif" }}>
                <strong>What Most Students Do (Wrong):</strong> {qt.example.wrongApproach}
              </p>
            </div>
          )}
          <h4 style={{ fontSize: '.95rem', fontWeight: 600, color: '#374151', margin: '0 0 .5rem 0', fontFamily: "'Arial', sans-serif" }}>Solution</h4>
          {qt.example.steps.map((step, i) => (
            <p key={`step-${i}`} style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .25rem 0', paddingLeft: '.5rem', fontFamily: "'Arial', sans-serif" }}>{step}</p>
          ))}
        </div>

        {/* Practice */}
        <PracticeReveal practice={qt.practice} idx={qt.number} />

        {/* Key Takeaway */}
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1rem' }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'flex-start', gap: '.4rem', fontFamily: "'Arial', sans-serif", color: '#92400e', fontWeight: 500, lineHeight: 1.5 }}>
            <Lightbulb size={16} style={{ marginTop: 3, flexShrink: 0 }} />
            <span><strong>Key Takeaway:</strong> {qt.keyTakeaway}</span>
          </p>
        </div>
      </div>
    </section>
  );
}

export default function LinearEquationsInequalities() {
  const contentRef = useRef(null);

  useEffect(() => {
    if (window.loadMathJax) window.loadMathJax();
    const timer = setInterval(() => {
      if (window.MathJax?.typesetPromise && contentRef.current) {
        window.MathJax.typesetPromise([contentRef.current]).catch(() => {});
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // Re-typeset when practice answers are revealed
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (window.MathJax?.typesetPromise && contentRef.current) {
        window.MathJax.typesetPromise([contentRef.current]).catch(() => {});
      }
    });
    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': 'Master the Most Common SAT Linear Equations and Inequalities Questions',
    'description': 'A comprehensive guide to solving SAT linear equations and inequalities faster with strategies, worked examples, and practice problems.',
    'author': { '@type': 'Organization', 'name': 'Practice4SAT', 'url': SITE_URL },
    'publisher': {
      '@type': 'Organization',
      'name': 'Practice4SAT',
      'url': SITE_URL,
      'logo': { '@type': 'ImageObject', 'url': 'https://www.practice4sat.com/assets/images/logo.png' }
    },
    'datePublished': '2026-04-13',
    'mainEntityOfPage': `${SITE_URL}/sat-tricks/linear-equations-inequalities`,
    'image': 'https://www.practice4sat.com/assets/images/logo.png'
  };

  return (
    <div className="App">
      <SEOHead
        title="Linear Equations & Inequalities - SAT Math Tricks & Strategies"
        description="Master the most common SAT linear equations and inequalities questions. Learn strategies for equations with fractions, variables on both sides, word problems, multi-step inequalities, and parameter questions."
        canonical="/sat-tricks/linear-equations-inequalities"
        keywords="SAT linear equations, SAT inequalities, SAT algebra tricks, SAT math strategies, equations with fractions SAT, variables on both sides SAT, SAT word problems, SAT multi-step inequalities, SAT parameter questions"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'SAT Tricks & Strategies', url: '/sat-tricks' },
          { name: 'Linear Equations & Inequalities' }
        ]}
      />

      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <Header variant="public" />

        <main style={{ flex: 1 }} ref={contentRef}>
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav" style={{ padding: '1rem 2rem 0', maxWidth: 1400, margin: '0 auto' }}>
            <ol style={{ display: 'flex', alignItems: 'center', gap: '.4rem', listStyle: 'none', padding: 0, margin: 0, fontSize: '.9rem', fontFamily: "'Arial', sans-serif" }}>
              <li>
                <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '.25rem' }}>
                  <Home size={14} aria-hidden="true" /><span>Home</span>
                </Link>
              </li>
              <li aria-hidden="true" style={{ color: '#9ca3af' }}><ChevronRight size={14} /></li>
              <li>
                <Link to="/sat-tricks" style={{ color: '#6b7280', textDecoration: 'none' }}>SAT Tricks & Strategies</Link>
              </li>
              <li aria-hidden="true" style={{ color: '#9ca3af' }}><ChevronRight size={14} /></li>
              <li><span aria-current="page" style={{ color: '#0030b3', fontWeight: 600 }}>Linear Equations & Inequalities</span></li>
            </ol>
          </nav>

          <article>
            {/* Title & Intro */}
            <div style={{ padding: '2rem 2rem 1rem', maxWidth: 1400, margin: '0 auto' }}>
              <h1 data-testid="subtopic-title" style={{ fontSize: '1.5rem', fontWeight: 600, textAlign: 'center', marginTop: 0, marginBottom: '.75rem', color: '#0030b3' }}>
                Master the Most Common SAT Linear Equations and Inequalities Questions
              </h1>
              <p style={{ color: '#374151', lineHeight: 1.6, marginBottom: '1rem', fontFamily: "'Arial', sans-serif" }}>
                Linear equations and inequalities are among the most frequently tested topics on the SAT Math section.
                This guide focuses on the exact question types you'll see on the real exam — and shows you how to solve them faster, avoid traps, and maximize accuracy.
              </p>
            </div>

            {/* Question Type Sections */}
            {questionTypes.map((qt, i) => (
              <QuestionTypeSection key={qt.number} qt={qt} idx={i} />
            ))}

            {/* Internal Links */}
            <aside style={{ maxWidth: 1400, margin: '2rem auto', padding: '1.5rem 2rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#374151', marginBottom: '.75rem', fontFamily: "'Arial', sans-serif" }}>
                Continue Your SAT Preparation
              </h2>
              <nav aria-label="Related SAT resources" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <Link to="/quick-quiz" data-testid="link-diagnostic-quiz" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '.95rem', fontFamily: "'Arial', sans-serif" }}>
                  Take the Free SAT Diagnostic Quiz
                </Link>
                <a href="/#pricing" data-testid="link-study-plan" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '.95rem', fontFamily: "'Arial', sans-serif" }}>
                  Get Your Personalized Study Plan
                </a>
              </nav>
            </aside>
          </article>
        </main>

        <Footer />
      </div>

      <style>{`
        @media (min-width: 768px) {
          .desktop-nav { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
        }
        @media (max-width: 767px) {
          .desktop-nav { display: none !important; }
        }
        .footer-link:hover { color: #91ccff !important; }
      `}</style>
    </div>
  );
}
